<template>
    <modal @close="modals.importWHOrdersModal = false">
        <h3>Import Orders</h3>
        <template v-if="screen === 'import'">
            <div class="large-form">

                <div class="form-line" style="text-align: left; margin-bottom: 20px">
                    <div class="form-field">
                        <p>You can use this tool to <b>upload a CSV of one or more orders</b> to be send directly to the Warehouse for fulfilment.</p>
                        Import instructions:
                        <ul>
                            <li><b>Use the <a title="Download the CSV template." class="action action-download" href="/pro/files/worksends_warehouse_import_template.csv" download style="color: #1d5997;">CSV template</a></b> to prepare your CSV file.</li>
                            <li><b>You have to use semicolons</b> '<b>;</b>' to separate your CSV values. Commas will not be accepted.</li>
                            <li><b>Required fields:</b> status, first_name, last_name, address_1, city, postcode, country, email, total, item_1_id, item_1_quantity</li>
                            <li><b>ID field is not required.</b> Use it only if you already have a remote ID for imported orders. If used it needs to be unique for every column.</li>
                            <li><b>Use status <i>processing</i></b> for all new orders ready for fulfilment.</li>
                        </ul>
                    </div>
                </div>
                
                <div class="form-line"  style="position:relative; margin-bottom: 20px;">
                    <div class="form-line-fields" style="width: 100%;">
                        <div id="form-field-base" class="form-field form-field-image-upload">
                            <label>CSV ORDERS FILE *</label>
                            <div @click="$refs.csvUploadInput.click()" class="ws-button">Upload CSV File</div>

                            <div class="file-upload-action-links">
                                <a title="Download the CSV template." class="action action-download" href="/pro/files/worksends_warehouse_import_template.csv" download>DOWNLOAD CSV TEMPLATE</a>
                            </div>

                            <div v-if="csvFile" class="file-upload-file">{{ csvFile.name }}</div>
                            <input ref="csvUploadInput" accept="text/csv" style="display: none" type="file" @change="onCsvChange">
                        </div>
                    </div>
                </div>

                <div class="form-line" v-if="importError" style="text-align: left; margin-bottom: 20px">
                    <div class="form-field">
                        <div v-if="importError === 'bad'" class="small-note error"><b>The CSV you imported appears to be malformed.</b> Please make sure you use the template above and only use semicolon <b>;</b> to separate the CSV values.</b> </div>
                        <div v-else-if="importError === 'empty'" class="small-note error"><b>The CSV you imported appears to be empty or malformed.</b> Please make sure you use the template above and only use semicolon <b>;</b> to separate the CSV values.</b> </div>
                        <div v-else-if="importError === 'missing'" class="small-note error"><b>Some required fields in the CSV are missing.</b> <p>First occurrence in {{ importErrorDetails}}</p> <i>The required fields are:  {{ requiredFields.join(', ') }}</i></div>
                        <div v-else-if="importError === 'bad-client'" class="small-note error"><b>You are not integrated into bulk order import system.</b> Please contact us to get set up.</div>
                    </div>
                </div>
                
                
            </div>

            <div class="form-actions">
                <div class="ws-button ws-button-secondary" data-action="cancel" @click="modals.importWHOrdersModal = false">Cancel</div>
                <div class="ws-button ws-button-main" @click="importOrders">Import Orders</div>
            </div>
        </template>
        
        <template v-else-if="screen === 'processing'">
            <div class="ws-form-processing"><div class="value-loader"></div><p>We are processing your request. <br>Please give us a moment...</p></div>
        </template>

        <template v-else-if="screen === 'complete'">
            <div class="large-form">
                <div class="form-line" style="margin-bottom: 40px">
                    <div class="form-field" style="font-size: 16px;margin-top: 35px;"><b>Success!</b> {{ importedOrdersCount }} orders have been imported.</div>
                </div>
            </div>
            <div class="form-actions">
                <div class="ws-button ws-button-secondary" data-action="close" @click="modals.importWHOrdersModal = false">Close</div>
            </div>
        </template>
        
        
    </modal>
</template>

<script>
import Modal from "@/components/modals/Modal.vue";
import axios from 'axios'

export default {
    name: "ImportWhOrdersModal",
    components: {Modal},
    data() {
        return {
            screen: 'import',
            csvFile:null,
            requiredFields: ['status','first_name','last_name','address_1','city','postcode','country','email','total','item_1_id','item_1_quantity'],
            importError: null,
            importErrorDetails: '',
            clientID: null,
            errors: {},
            response: {data: null},
            importedOrdersCount: 0,
        }
    },
    methods: {
        async importOrders() {
            if (!this.csvFile) return;
            
            this.screen = 'processing';
            const reader = new FileReader();
            reader.onload = async (res) => {
                this.importError = null;
                const importData = this.parseCSVtoJson(res.target.result);
                if (!importData.length || importData[0]===null || typeof importData[0] !== 'object') {
                    this.importError= 'empty';
                } else if (!this.checkRequiredFields(importData)) {
                    this.importError = 'missing';
                } else if (!this.checkClientID) {
                    this.importError = 'bad-client';
                }
                if (this.importError) {
                    this.screen = 'import';
                }
                try {
                    this.response = await this.dispatchWaitAndNotify('importWarehouseOrders', {create: importData,});
                    if (this.response.result === 'success') {
                        this.importedOrdersCount = this.response.processed_items;
                        this.screen = 'complete';
                        this.$emit('imported');
                    } else {
                        this.importError = 'bad';
                        this.screen = 'import';
                    }
                } catch (e) {
                    this.screen = 'import';
                }
            };
            reader.onerror = (err) => {
                console.log(err);
                this.importError= 'bad';
                this.screen = 'import';
            };
            reader.readAsText(this.csvFile);
        },
        parseCSVtoJson(file) {
            const lines = file.split('\n') // 1️⃣
            const header = lines[0].trim().split(';') // 2️⃣
            return lines.slice(1).filter(line => {
                return line.trim().split(';').length >= 2; 
            }).map(line => {
                const fields = line.trim().split(';') // 3️⃣
                if (fields.length < 2) return;
                return Object.fromEntries(header.map((h, i) => [h, fields[i]])) // 4️⃣
            })
        },
        checkRequiredFields(data) {
            data.forEach((row, index) => {
                let entries = Object.entries(row);
                entries.forEach(item => {
                    if (this.requiredFields.includes(item[0]) && item[1].trim() === '') {
                        console.log(item);
                        this.importErrorDetails = 'line ' + (index+2) + ', field ' + item[0];
                        return false;
                    }
                });
            });
            return true;
        },
        checkClientID() {
            if (this.$store.state.team?.detail?.slug==='ADHD') this.clientID = 8;
            if (this.$store.state.team?.detail?.slug==='dev-test') this.clientID = 9;
            else return false;
            return true;
        },
        onCsvChange(event) {
            this.csvFile = event.target.files[0];
        },
    },
    computed: {
        modals() {
            return this.$store.state.modals;
        },
        user() {
            return this.$store.state.currentUser;
        },
    }
}
</script>

<style scoped>

</style>